import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { userSelector } from '@src/selectors/app'
import { Intercom } from '@src/utils'
import {
  RootStackParamList,
  QuestionnaireScreenName,
  QuestionnaireStackParamList,
} from '@src/navigation/types'

const INTERCOM_INELIGIBLE_ROUTE_NAMES: (keyof RootStackParamList)[] = [
  'CallScheduling',
  'ClaimFreeVideoCallModal',
  'EligibilityCheck',
  'PaidCallsIntroScreen',
  'RescheduleOrCancelVideoCallModal',
  'CancelRecurringAppointmentModal',
  'SteloSelectSubscription',
  'SubscriptionCheckout',
  'VideoCallModal',
  'VideoCallCheckout',
  'VideoCallsGettingStartedScreen',
  'VideoCallsStartModal',
  ...(Object.keys(QuestionnaireScreenName) as (keyof QuestionnaireStackParamList)[]),
]

export const isIntercomIneligibleRouteName = (routeName: string | null) =>
  !!routeName && INTERCOM_INELIGIBLE_ROUTE_NAMES.includes(routeName as keyof RootStackParamList)

// enable or disable intercom based on app state
export const useSetIntercomUser = ({
  hasAppAccess,
  hasIneligibleIntercomRoute,
}: {
  hasAppAccess: boolean
  hasIneligibleIntercomRoute: boolean
}) => {
  const user = useSelector(userSelector)

  const disableIntercom = !hasAppAccess || hasIneligibleIntercomRoute

  useEffect(() => {
    const setIntercomUser = async () => {
      if (!user?.id) {
        return
      }

      const intercomUserProperties = {
        id: user.id,
        email: user.email,
        fullName: user.fullName,
        intercomDigest: user.intercomDigest,
      }

      try {
        await Intercom.setUser(intercomUserProperties)
      } catch {
        // Intercom user is already set
      }
    }

    const logoutIntercom = async () => {
      try {
        await Intercom.reset()
      } catch {
        // Intercom user is already removed
      }
    }

    if (disableIntercom) {
      logoutIntercom()
    } else {
      setIntercomUser()
    }
  }, [disableIntercom, user?.email, user?.fullName, user?.id, user?.intercomDigest])
}
